import React, { useEffect, useMemo, useState, useRef } from "react";
import Stats from "../../Components/Stats";
import CustomTable from "../../Components/CustomTable";
import { Pencil, X } from "lucide-react";
import { getLicenseList, updateLicenseStatus } from "../../api";
import { Loader } from "../../Components/Loader";
import moment from "moment";
import { ToastMsg } from "../../Components/ToastMsg";

const normalizeLicenses = (items) => {
  if (!Array.isArray(items)) return [];
  return items.map((item) => {
    return {
      id: item.id,
      name: item.user_name || "N/A", // if API later includes user name, else use placeholder
      licenseNumber: item.licence_number || "N/A",
      expiryDate: item.expiry_date
        ? moment(item.expiry_date).format("DD MMM YYYY")
        : "N/A",
      licenseFront: item.front_image_url,
      licenseBack: item.back_image_url,
      status:
        item.admin_response || (item.is_verified ? "Approved" : "Pending"),
      actionType:
        item.is_verified === null
          ? "pending"
          : item?.is_verified
          ? "approved"
          : "reject",
      reason: item.rejection_reason || "", // if backend provides rejection reason later
    };
  });
};

export default function LicenseManagement() {
  const [globalFilter, setGlobalFilter] = useState("");
  const [pageIndex, setPageIndex] = useState(1);
  const pageSize = 3;
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [startDate, setStartDate] = useState(moment().startOf("month").format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));

  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(null);
  const [pageData, setPageData] = useState("");
  const[currentId,setCurrentId]=useState("")
  const[currentStatus,setCurrnetStatus]=useState()

  const debounceTimeoutRef = useRef(null);

  // ====== API Fetch ======
  const fetchLicenses = async ({
    start = startDate,
    end = endDate,
    isSearch,
  }) => {
    try {
      setError("");
      setLoading(!isSearch);

      const res = await getLicenseList({
        search: globalFilter,
        start_date: start,
        end_date: end,
        page: pageIndex,
      });

      setLicenses(res?.data?.data || []);
      setPageData(res?.data?.page_data);
    } catch (e) {
      setError(e.message || "Failed to load licenses");
      setLicenses([]);
    } finally {
      setLoading(false);
    }
  };

  // Debounced search
  useEffect(() => {
    if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);

    debounceTimeoutRef.current = setTimeout(() => {
      fetchLicenses({ start: startDate, end: endDate, isSearch: true });
    }, 500);

    return () => clearTimeout(debounceTimeoutRef.current);
  }, [globalFilter]);

  // Initial load + page change
  useEffect(() => {
    fetchLicenses({ start: startDate, end: endDate });
  }, [pageIndex]);

  // ====== Reject Modal ======
  const openRejectModal = (type,id) => {
    setCurrentId(id)
    setIsRejectModalOpen(true);
    setRejectReason("");
    setCurrnetStatus(type)
  };
  const closeRejectModal = () => {
    setIsRejectModalOpen(false);
    setRejectReason("");
  };
  const handleRejectSubmit = async (status="reject",id=currentId) => {
    
    const status_ = status||currentStatus;
    const id_= id||currentId  
    try {
      const data =status_==="reject"? { status: status_, admin_response: rejectReason }: { status: status_ };
      setLoading(true);
      const res = await updateLicenseStatus(data,id_);
      ToastMsg(res?.data?.message, "success");
      fetchLicenses({ start: startDate, end: endDate });
      setLoading(false);
    } catch (err) {
      ToastMsg(err?.response?.data?.messgae, "error");
    } finally {
      setLoading(false);
      closeRejectModal();
    }
  };

  // ====== Columns ======
  const columns = useMemo(
    () => [
      {
        key: "sno",
        label: "S.No.",
        minWidth: "36px",
        width: "36px",
        render: (_, __, idx) => pageIndex * pageSize - pageSize + idx + 1,
        showSortIcon: false,
      },
      { key: "name", label: "NAME", minWidth: "110px", width: "110px" },
      {
        key: "licenseNumber",
        label: "LICENSE NUMBER",
        minWidth: "159px",
        width: "159px",
      },
      {
        key: "expiryDate",
        label: "EXPIRY DATE",
        minWidth: "159px",
        width: "159px",
      },
      {
        key: "licenseView",
        label: "LICENSE VIEW",
        minWidth: "200px",
        width: "200px",
        render: (_, row) => (
          <div className="flex gap-6 items-start">
            {/* Front */}
            <div className="flex items-center gap-3">
              <div className="flex flex-col justify-between">
                <img
                  src={row.licenseFront}
                  alt="Front"
                  className="w-[64px] h-[37.65px] object-cover rounded"
                />
                <span className="text-xs text-[#6C6C6C] font-medium">
                  Front
                </span>
              </div>
              <a
                href={row.licenseFront}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-[#F6C244] font-medium underline"
              >
                View
              </a>
            </div>

            {/* Back */}
            <div className="flex items-center gap-3">
              <div className="flex flex-col justify-between">
                <img
                  src={row.licenseBack}
                  alt="Back"
                  className="w-[64px] h-[37.65px] object-cover rounded"
                />
                <span className="text-xs text-[#6C6C6C] font-medium">Back</span>
              </div>
              <a
                href={row.licenseBack}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-[#F6C244] font-medium underline"
              >
                View
              </a>
            </div>
          </div>
        ),
      },
      {
        key: "actions",
        label: "ACTIONS",
        minWidth: "180px",
        width: "180px",
        render: (_, row, idx) => {
          if (row.actionType === "pending") {
            return (
              <div className="flex gap-2">
                <button
                  className="border border-[#34C759] text-[#34C759] px-4 py-1 rounded hover:bg-[#34C759] hover:text-black transition"
                  onClick={() => {
                    handleRejectSubmit("approve", row?.id);
                  }}
                >
                  Approve
                </button>
                <button
                  className="border border-[#FF3B30] text-[#FF3B30] px-4 py-1 rounded hover:bg-[#FF3B30] hover:text-white transition"
                  onClick={() => {
                    openRejectModal("reject", row?.id);
                  }}
                >
                  Reject
                </button>
              </div>
            );
          }
          if (row.actionType === "approved" || row.actionType === "rejected") {
            return (
              <div className="relative flex items-center gap-2">
                <span
                  className={
                    row.actionType === "approved"
                      ? "text-[#34C759]"
                      : "text-[#FF3B30]"
                  }
                >
                  {row.actionType === "approved" ? "Approved" : "Rejected"}
                </span>
                {/* <button
                  className="focus:outline-none"
                  onClick={() =>
                    setIsDropdownOpen(isDropdownOpen === idx ? null : idx)
                  }
                >
                  <Pencil className="text-[#F3B753] w-4 h-4" />
                </button> */}
                {isDropdownOpen === idx && (
                  <div className="absolute top-8 right-0 z-50 w-40 rounded-xl bg-[#232323] shadow-lg flex flex-col p-4">
                    <button
                      className="text-[#bdbdbd] text-lg font-semibold mb-2 cursor-not-allowed opacity-50 text-left"
                      disabled={row?.actionType==="approved"}
                      onClick={() => {
                        setIsDropdownOpen(null);
                        handleRejectSubmit("approve",row?.id);
                      }}
                    >
                      Approve
                    </button>
                    <div className="h-px w-full my-2 bg-gradient-to-r from-[#F9AC1D40] via-[#F6E19B0D] to-transparent" />
                    <button
                      className="text-white text-lg font-semibold text-left hover:text-[#F3B753] transition"
                      disabled={row?.actionType!=="approved"}
                      onClick={() => {
                        setIsDropdownOpen(null);
                        openRejectModal("reject",row?.id);
                      }}
                    >
                      Reject
                    </button>
                  </div>
                )}
              </div>
            );
          }
          return null;
        },
      },
    ],
    [pageIndex, pageSize, isDropdownOpen]
  );

  if (loading) return <Loader />;
 
  return (
    <div className="flex flex-col gap-4 p-8 bg-[#161616] min-h-screen">
      <Stats
        handleStartDate={(date) =>
          setStartDate(moment(date).format("YYYY-MM-DD"))
        }
        handleEndDate={(date) => setEndDate(moment(date).format("YYYY-MM-DD"))}
        handlegetData={() => fetchLicenses({ start: startDate, end: endDate })}
        startDate={startDate}
        endDate={endDate}
        showtiles={false}
        
      />
      {error && <span className="text-xs text-red-400">{error}</span>}

      <CustomTable
        data={normalizeLicenses(licenses)}
        columns={columns}
        pageIndex={pageIndex}
        setPageIndex={setPageIndex}
        pageSize={pageSize}
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
        disableClientFilter={true}
        pageData={pageData}
      />

      {/* Reject Modal */}
      {isRejectModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center  bg-black bg-opacity-70">
          <div className="relative border border-[#F9AC1D] bg-gradient-to-b from-[#2b220fdb] to-[#18140bec] rounded-xl shadow-lg w-[480px] py-4 px-8 flex flex-col items-center">
            <div className="w-full flex flex-row justify-between items-center mb-4">
              <h2 className="text-2xl font-semibold text-white">
                Reason for Rejection
              </h2>
              <button
                className=" text-[#F3B753] hover:text-yellow-400"
                onClick={closeRejectModal}
                aria-label="Close"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <textarea
              className="w-full h-[160px] rounded-lg p-4 text-base text-[#18140B] placeholder-[#BDBDBD] focus:outline-none mb-10"
              placeholder="Add Reason for Rejection"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value.slice(0, 500))}
              style={{ resize: "none" }}
            />
            <div className="flex w-full gap-10 justify-between">
              <button
                className="flex-1 border border-[#F3B753] text-[#F3B753] py-3 rounded-lg font-semibold text-lg hover:bg-[#F3B753] hover:text-[#18140B] transition"
                onClick={closeRejectModal}
              >
                Cancel
              </button>
              <button
                className="flex-1 bg-[#F3B753] text-[#18140B] py-3 rounded-lg font-semibold text-lg hover:bg-[#e6a800] transition"
                onClick={()=>handleRejectSubmit("reject",currentId)}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
